/**
 * AuthoringChatPanel — Content Studio chat with artifact review and approval.
 *
 * Uses POST /scenario-os/coach/authoring-chat/stream so that the
 * ScenarioOSOrchestrator generates structured curriculum artifacts.
 * Artifacts are rendered inline via StructuredArtifactCard with
 * Approve / Edit / Reject / Regenerate actions.
 *
 * The coach can also import existing Content Library items for editing.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StructuredArtifactCard from '../../components/StructuredArtifactCard';
import ReasoningPanel from '../../../../shared/components/Reasoning/ReasoningPanel';
import { useReasoningStream } from '../../../../shared/hooks/useReasoningStream';

const API_BASE = process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os';

function getToken() {
  return localStorage.getItem('scenario_os_token') || '';
}

const CONTENT_TYPE_ICONS = {
  video: '🎥', slide_deck: '📊', quiz_panel: '🧪', ai_lab: '🔬',
  reflection: '💭', curated_resource: '📚', prompt_canvas: '✏️',
  content_panel: '📋', scenario: '🎬', module: '📦',
};

/**
 * Extract JSON-embedded artifacts from agent reply text.
 * The LLM sometimes outputs artifacts as raw JSON or inside ```json
 * code blocks instead of through the structured artifact pipeline.
 * Returns { cleanText, extractedArtifacts }.
 */
function extractEmbeddedArtifacts(text) {
  if (!text || typeof text !== 'string') return { cleanText: text, extractedArtifacts: [] };

  const extracted = [];
  let cleanText = text;

  // Pattern 1: ```json ... ``` code blocks containing artifact JSON
  const codeBlockRe = /```(?:json)?\s*\n?\s*(\{[\s\S]*?"artifacts"\s*:\s*\[[\s\S]*?\][\s\S]*?\})\s*\n?\s*```/gi;
  let match;
  while ((match = codeBlockRe.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      const arts = parsed.artifacts || (Array.isArray(parsed) ? parsed : [parsed]);
      for (const a of arts) {
        if (a.artifact_type || a.content_type) {
          if (!a.artifact_id) a.artifact_id = `extracted_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          if (!a.status) a.status = 'pending';
          if (!a.display_name) a.display_name = a.data?.title || a.title || a.artifact_type || 'Content';
          extracted.push(a);
        }
      }
      cleanText = cleanText.replace(match[0], '').trim();
    } catch { /* not valid JSON, leave in text */ }
  }

  // Pattern 2: raw JSON starting with {"artifacts": or {"artifact_type":
  if (extracted.length === 0) {
    const rawJsonRe = /(\{"(?:artifacts|artifact_type)"[\s\S]*\})\s*$/;
    const rawMatch = cleanText.match(rawJsonRe);
    if (rawMatch) {
      try {
        const parsed = JSON.parse(rawMatch[1]);
        const arts = parsed.artifacts || [parsed];
        for (const a of arts) {
          if (a.artifact_type || a.content_type) {
            if (!a.artifact_id) a.artifact_id = `extracted_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            if (!a.status) a.status = 'pending';
            if (!a.display_name) a.display_name = a.data?.title || a.title || a.artifact_type || 'Content';
            extracted.push(a);
          }
        }
        cleanText = cleanText.replace(rawMatch[0], '').trim();
      } catch { /* not valid JSON */ }
    }
  }

  if (!cleanText && extracted.length > 0) {
    cleanText = "Here's the updated content. Review each item below and approve, edit, or ask me to refine further.";
  }

  return { cleanText, extractedArtifacts: extracted };
}

// TeachIQ brand palette: #1E1E24 (dark bg), #92140C (crimson), #FFF8F0 (cream), #FFCF99 (amber)
const BRAND = {
  dark:    '#1E1E24',
  crimson: '#92140C',
  cream:   '#FFF8F0',
  amber:   '#FFCF99',
  border:  'rgba(146,20,12,0.25)',   // crimson at 25% opacity
  subtext: '#7a6b5d',                // warm gray on cream
  agentBg: '#ffffff',
};

const S = {
  page: {
    display: 'flex', flexDirection: 'column',
    height: '100%', minHeight: 'calc(100vh - 80px)',
    background: BRAND.cream, color: BRAND.dark, fontFamily: 'inherit',
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 20px', borderBottom: `1px solid ${BRAND.border}`,
    background: BRAND.dark, flexShrink: 0, gap: 12,
  },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, color: BRAND.cream },
  domainSelect: {
    background: 'rgba(255,248,240,0.1)', border: `1px solid ${BRAND.amber}`, borderRadius: 6,
    color: BRAND.cream, padding: '6px 12px', fontSize: 13, outline: 'none', cursor: 'pointer',
  },
  newSessionBtn: {
    background: 'transparent', border: `1px solid ${BRAND.amber}`, borderRadius: 6,
    color: BRAND.amber, padding: '6px 14px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  importBtn: {
    background: 'transparent', border: `1px solid ${BRAND.crimson}`, borderRadius: 6,
    color: BRAND.cream, padding: '6px 14px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
    backgroundColor: BRAND.crimson,
  },
  errorBanner: {
    background: 'rgba(146,20,12,0.08)', border: `1px solid rgba(146,20,12,0.3)`,
    borderRadius: 6, color: BRAND.crimson, padding: '8px 16px', fontSize: 13,
    margin: '8px 20px 0', flexShrink: 0,
  },
  successBanner: {
    background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.3)',
    borderRadius: 6, color: '#059669', padding: '8px 16px', fontSize: 13,
    margin: '8px 20px 0', flexShrink: 0,
  },
  thread: {
    flex: 1, overflowY: 'auto', padding: '16px 24px',
    display: 'flex', flexDirection: 'column', gap: 12,
    background: BRAND.cream,
  },
  bubbleWrap: (align) => ({
    display: 'flex', justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    maxWidth: '100%',
  }),
  bubble: (role) => {
    const base = {
      maxWidth: '75%', borderRadius: 12, padding: '10px 14px',
      fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word', whiteSpace: 'pre-wrap',
    };
    if (role === 'user' || role === 'coach') {
      return { ...base, background: BRAND.crimson, color: BRAND.cream, borderBottomRightRadius: 2 };
    }
    return { ...base, background: BRAND.agentBg, color: BRAND.dark, border: `1px solid ${BRAND.border}`, borderBottomLeftRadius: 2 };
  },
  roleLabel: (role) => ({
    fontSize: 11, fontWeight: 700, marginBottom: 4,
    color: (role === 'user' || role === 'coach') ? 'rgba(255,248,240,0.8)' : BRAND.subtext,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }),
  artifactSection: {
    maxWidth: '85%', width: '100%',
  },
  artifactSectionHeader: {
    fontSize: 12, fontWeight: 700, color: BRAND.subtext, marginBottom: 8,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', flex: 1, color: BRAND.subtext, fontSize: 14,
    gap: 8, padding: 32, textAlign: 'center', background: BRAND.cream,
  },
  inputBar: {
    display: 'flex', padding: '12px 20px', gap: 10,
    borderTop: `1px solid ${BRAND.border}`, background: BRAND.dark, flexShrink: 0,
  },
  input: {
    flex: 1, background: 'rgba(255,248,240,0.08)', border: `1px solid ${BRAND.amber}`, borderRadius: 8,
    color: BRAND.cream, padding: '10px 14px', fontSize: 14, outline: 'none',
    fontFamily: 'inherit', resize: 'none', lineHeight: 1.5,
  },
  sendBtn: (disabled) => ({
    background: disabled ? 'rgba(255,207,153,0.2)' : BRAND.crimson,
    color: disabled ? BRAND.amber : BRAND.cream,
    border: 'none', borderRadius: 8, padding: '10px 20px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14, fontWeight: 600, flexShrink: 0, transition: 'all 0.15s',
  }),
  streamingIndicator: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
    background: BRAND.agentBg, border: `1px solid ${BRAND.border}`, borderRadius: 12,
    maxWidth: '75%', fontSize: 13, color: BRAND.subtext, borderBottomLeftRadius: 2,
  },
  streamingDot: {
    width: 8, height: 8, borderRadius: '50%', background: BRAND.crimson,
    animation: 'pulse 1s infinite',
  },
  usageSummary: {
    display: 'flex', gap: 16, padding: '6px 16px', fontSize: 11, color: BRAND.subtext,
    background: 'rgba(255,207,153,0.15)', borderRadius: 6, marginTop: 4, flexWrap: 'wrap',
  },
  // Library picker overlay
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(30,30,36,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  pickerModal: {
    background: BRAND.cream, border: `1px solid ${BRAND.border}`, borderRadius: 12,
    width: '90%', maxWidth: 680, maxHeight: '80vh', display: 'flex',
    flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(30,30,36,0.3)',
  },
  pickerHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', borderBottom: `1px solid ${BRAND.border}`, flexShrink: 0,
    background: BRAND.dark,
  },
  pickerTitle: { fontSize: 16, fontWeight: 700, color: BRAND.cream },
  pickerClose: {
    background: 'none', border: 'none', color: BRAND.amber, fontSize: 20,
    cursor: 'pointer', padding: '4px 8px',
  },
  pickerSearch: {
    background: '#fff', border: `1px solid ${BRAND.border}`, borderRadius: 6,
    color: BRAND.dark, padding: '8px 14px', fontSize: 13, outline: 'none',
    width: '100%', margin: '12px 20px', boxSizing: 'border-box',
  },
  pickerList: {
    flex: 1, overflowY: 'auto', padding: '0 20px 12px', background: BRAND.cream,
  },
  pickerItem: (isSelected) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    borderRadius: 8, cursor: 'pointer', marginBottom: 4, transition: 'all 0.1s',
    background: isSelected ? 'rgba(146,20,12,0.08)' : '#fff',
    border: isSelected ? `1px solid ${BRAND.crimson}` : `1px solid ${BRAND.border}`,
  }),
  pickerItemIcon: { fontSize: 20, flexShrink: 0, width: 28, textAlign: 'center' },
  pickerItemInfo: { flex: 1, minWidth: 0 },
  pickerItemTitle: { fontSize: 14, fontWeight: 600, color: BRAND.dark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  pickerItemMeta: { fontSize: 11, color: BRAND.subtext, marginTop: 2 },
  pickerFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px', borderTop: `1px solid ${BRAND.border}`, flexShrink: 0,
    background: BRAND.cream,
  },
};

// ── Library Picker Modal ──────────────────────────────────────────────────────

function LibraryPickerModal({ domain, onImport, onClose }) {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`${API_BASE}/coach/scenes?domain_id=${encodeURIComponent(domain)}&limit=500`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        if (!mounted) return;
        const list = data.scenes || data.items || (Array.isArray(data) ? data : []);
        setScenes(list);
      })
      .catch(() => { if (mounted) setScenes([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [domain]);

  const filtered = scenes.filter((s) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    const name = s.display_name || s.title || s.name || '';
    return name.toLowerCase().includes(q)
      || (s.scene_type || '').toLowerCase().includes(q)
      || (s.content_type || '').toLowerCase().includes(q);
  });

  function toggleSelect(sceneId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  }

  function handleImport() {
    if (selected.size === 0) return;
    onImport([...selected]);
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.pickerModal} onClick={(e) => e.stopPropagation()}>
        <div style={S.pickerHeader}>
          <span style={S.pickerTitle}>Import from Content Library</span>
          <button style={S.pickerClose} onClick={onClose}>&times;</button>
        </div>

        <div style={{ padding: '0 20px' }}>
          <input
            style={{ ...S.pickerSearch, margin: '12px 0' }}
            placeholder="Search by title or content type…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
        </div>

        <div style={S.pickerList}>
          {loading && <div style={{ color: '#94a3b8', fontSize: 13, padding: 12 }}>Loading content…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 13, padding: 12, textAlign: 'center' }}>
              {filter ? 'No matching content found.' : 'No content in this domain yet.'}
            </div>
          )}
          {filtered.map((scene) => {
            const ct = scene.content_type || scene.scene_type || 'scenario';
            const icon = CONTENT_TYPE_ICONS[ct] || '📎';
            const isSelected = selected.has(scene.scene_id);
            return (
              <div
                key={scene.scene_id}
                style={S.pickerItem(isSelected)}
                onClick={() => toggleSelect(scene.scene_id)}
              >
                <div style={S.pickerItemIcon}>{icon}</div>
                <div style={S.pickerItemInfo}>
                  <div style={S.pickerItemTitle}>{scene.display_name || scene.title || scene.name || scene.scene_id}</div>
                  <div style={S.pickerItemMeta}>
                    {ct.replace(/_/g, ' ')}
                    {scene.difficulty ? ` · ${scene.difficulty}` : ''}
                    {scene.status ? ` · ${scene.status}` : ''}
                  </div>
                </div>
                <div style={{
                  width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                  border: isSelected ? '2px solid #4f8ef7' : '2px solid #2d3748',
                  background: isSelected ? '#4f8ef7' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 12, fontWeight: 700,
                }}>
                  {isSelected ? '✓' : ''}
                </div>
              </div>
            );
          })}
        </div>

        <div style={S.pickerFooter}>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            {selected.size} item{selected.size !== 1 ? 's' : ''} selected
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...S.newSessionBtn, color: '#94a3b8', borderColor: '#2d3748' }}
              onClick={onClose}
            >Cancel</button>
            <button
              style={{
                background: selected.size > 0 ? '#8b5cf6' : '#2d3748',
                color: selected.size > 0 ? '#fff' : '#64748b',
                border: 'none', borderRadius: 6, padding: '6px 18px',
                fontSize: 13, fontWeight: 600,
                cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
              }}
              onClick={handleImport}
              disabled={selected.size === 0}
            >Import & Edit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AuthoringChatPanel() {
  const navigate = useNavigate();

  // Pre-fill from query params: ?domain=ai_orchestrator&prompt=Generate+5+items...&artifact_type=cat_item
  const searchParams = new URLSearchParams(window.location.search);
  const urlDomain       = searchParams.get('domain') || '';
  const urlPrompt       = searchParams.get('prompt') || '';
  const urlArtifactType = searchParams.get('artifact_type') || '';

  const [domain, setDomain] = useState(urlDomain);
  const [domainOptions, setDomainOptions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  // Pre-fill input from URL param so coach just clicks Send
  const [input, setInput] = useState(urlPrompt);
  const [streaming, setStreaming] = useState(false);
  // Bump this counter after each completed turn to refresh session history
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const {
    reasoningSteps, liveText, toolActions, isStreaming: reasoningStreaming,
    processSSELine, reset: resetReasoning,
  } = useReasoningStream();
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // ── Publish-to-module state ──────────────────────────────────────────────
  const [domainModules, setDomainModules] = useState([]);
  const [publishModal, setPublishModal] = useState({
    open: false, artifactIds: [], selectedModuleId: '', selectedTutorialId: '',
  });

  const threadRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/curriculum/domains`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then(({ domains }) => {
        if (!mounted) return;
        const authoring = (Array.isArray(domains) ? domains : []).filter(
          (d) => d.can_author !== false,
        );
        const opts = authoring.map((d) => ({
          value: d.domain_id,
          label: d.display_name || d.domain_id,
        }));
        setDomainOptions(opts);
        // Honour URL ?domain= param; otherwise fall back to first option
        setDomain((prev) => {
          if (prev && opts.some((o) => o.value === prev)) return prev;
          return opts.length > 0 ? opts[0].value : '';
        });
      })
      .catch(() => { if (mounted) setDomainOptions([]); });
    return () => { mounted = false; };
  }, []);

  // Load modules for the domain so the publish modal can show a picker
  useEffect(() => {
    if (!domain) return;
    let mounted = true;
    fetch(`${API_BASE}/coach/authoring-chat/domains/${domain}/modules`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => { if (mounted) setDomainModules(data.modules || []); })
      .catch(() => { if (mounted) setDomainModules([]); });
    return () => { mounted = false; };
  }, [domain]);

  // Load session history — refresh when domain changes, session ID changes
  // (first turn in a new session), or when a turn completes (historyRefresh).
  useEffect(() => {
    if (!domain) return;
    let mounted = true;
    fetch(`${API_BASE}/coach/authoring-chat/sessions`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        if (!mounted) return;
        // Backend already filters by coach's authoring_domains; we just
        // further narrow to the active domain and exclude abandoned sessions.
        const sessions = (data.sessions || []).filter(
          (s) => s.domain === domain && s.status !== 'abandoned',
        );
        setSessionHistory(sessions);
      })
      .catch(() => {});
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, sessionId, historyRefresh]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, artifacts, streaming, reasoningSteps, liveText]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  function startNewSession() {
    setSessionId(null);
    setMessages([]);
    setArtifacts([]);
    resetReasoning();
    setError(null);
    setSuccessMsg(null);
    setUsage(null);
    setShowHistory(false);
  }

  async function loadSession(sid) {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/coach/authoring-chat/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const session = await res.json();
      setSessionId(session.session_id);
      setMessages(session.messages || []);
      setArtifacts(session.pending_artifacts || []);
      setShowHistory(false);
      resetReasoning();
      setUsage(null);
    } catch (err) {
      setError(`Failed to load session: ${err.message}`);
    }
  }

  // ── Import from Content Library ─────────────────────────────────────────────

  async function handleLibraryImport(sceneIds) {
    setShowPicker(false);
    setImporting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/coach/authoring-chat/sessions/import-from-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ scene_ids: sceneIds, domain }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      setSessionId(data.session_id);
      setArtifacts(data.artifacts || []);
      setMessages([{
        role: 'system',
        content: `Imported ${(data.artifacts || []).length} item(s) from the Content Library. You can review, edit, or chat with the agent to refine this content.`,
        artifacts: data.artifacts || [],
        timestamp: new Date().toISOString(),
      }]);
      setSuccessMsg('Content imported. Edit inline or chat to refine with the agent.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  }

  // ── Send message via authoring-chat SSE ─────────────────────────────────────

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    setError(null);
    setSuccessMsg(null);
    resetReasoning();
    setUsage(null);

    const userMsg = { role: 'coach', content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API_BASE}/coach/authoring-chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          domain,
          message: text,
          session_id: sessionId || null,
        }),
        signal: ctrl.signal,
      });

      if (!res.ok) {
        const errText = await res.text();
        if (res.status === 403) {
          let detail = errText;
          try { detail = JSON.parse(errText).detail || errText; } catch {}
          throw new Error(detail);
        }
        throw new Error(`${res.status}: ${errText}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // Track whether the done event was successfully processed this turn.
      // If it never fires (parse error, stream cut, etc.) we show a fallback.
      let doneReceived = false;
      // Track accumulated delta text to detect when LLM has shifted to
      // generating JSON artifact output. Once in JSON mode, deltas are
      // suppressed from the CoT panel (artifacts appear after done fires).
      let deltaAccum = '';
      let inJsonArtifactMode = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop();

        for (const frame of frames) {
          if (!frame.trim()) continue;
          const eventMatch = frame.match(/^event:\s*(.+)$/m);
          // dataMatch: capture everything after "data: " on that line.
          // Use a simple indexOf-based extract to avoid regex size limits on
          // very large done payloads (reasoning_trace can be tens of KB).
          if (!eventMatch) continue;
          const dataPrefix = '\ndata: ';
          const dataStart = frame.indexOf(dataPrefix);
          if (dataStart < 0) continue;
          const rawData = frame.slice(dataStart + dataPrefix.length).trim();

          const evt = eventMatch[1].trim();
          let payload;
          try { payload = JSON.parse(rawData); } catch {
            // If JSON parse failed on the done event, log and skip rather than
            // silently dropping — the backend always ensures reply_text is set.
            console.warn('[AuthoringChat] SSE JSON parse failed for event:', evt, rawData.slice(0, 200));
            continue;
          }

          // Route all SSE events through the reasoning stream hook so it
          // stays in sync (especially the done event flushes live text).
          // Exception: suppress raw LLM delta tokens that are part of a JSON
          // artifact block — they create confusing "JSON block" display in the
          // CoT panel; artifacts will appear as proper cards after done fires.
          if (evt === 'reasoning' || evt === 'thinking' || evt === 'tool_use' || evt === 'status' || evt === 'done') {
            if (evt === 'reasoning' && payload.kind === 'delta') {
              const token = payload.text || '';
              deltaAccum += token;
              // Detect shift into JSON artifact mode: the accumulated delta
              // text starts with { after trimming leading prose/whitespace.
              // Once detected, suppress all subsequent deltas until done.
              if (!inJsonArtifactMode) {
                const trimmed = deltaAccum.trimStart();
                if (trimmed.startsWith('{') && (
                  trimmed.includes('"artifacts"') ||
                  trimmed.includes('"artifact_type"') ||
                  trimmed.includes('"content_type"')
                )) {
                  inJsonArtifactMode = true;
                  // Emit a status step so the user sees something meaningful
                  processSSELine('status', { step: '⚙️ Generating content structure…' });
                }
              }
              // Only forward prose deltas to the CoT panel
              if (!inJsonArtifactMode) {
                processSSELine(evt, payload);
              }
            } else {
              if (evt === 'done') {
                // Reset JSON mode state for the next turn
                deltaAccum = '';
                inJsonArtifactMode = false;
              }
              processSSELine(evt, payload);
            }
          }

          if (evt === 'done') {
            doneReceived = true;
            if (payload.session_id) setSessionId(payload.session_id);

            let replyText = payload.reply || '';
            let replyArtifacts = payload.artifacts || [];

            // When the LLM embeds artifact JSON in its text reply
            // (e.g. inside ```json blocks), extract and render them
            // as proper StructuredArtifactCards instead of raw text.
            if (replyText && replyArtifacts.length === 0) {
              const { cleanText, extractedArtifacts } = extractEmbeddedArtifacts(replyText);
              if (extractedArtifacts.length > 0) {
                replyText = cleanText;
                replyArtifacts = extractedArtifacts;
              }
            }

            // ALWAYS add the agent reply to the chat thread.
            // The backend guarantees reply is non-empty, but we guard with
            // a fallback to prevent ever showing a blank turn.
            const displayContent = replyText
              || (replyArtifacts.length > 0 ? "Here's the content I've created. Review each item below and approve, edit, or ask me to refine." : null)
              || "Generation complete. Your content has been saved to this session.";

            setMessages((prev) => [...prev, {
              role: 'assistant',
              content: displayContent,
              artifacts: replyArtifacts,
              timestamp: new Date().toISOString(),
            }]);

            if (replyArtifacts.length > 0) {
              setArtifacts((prev) => {
                const existingIds = new Set(prev.map((a) => a.artifact_id));
                const newArtifacts = replyArtifacts.filter((a) => !existingIds.has(a.artifact_id));
                return [...prev, ...newArtifacts];
              });
            }

            if (payload.usage) setUsage(payload.usage);
            // Trigger session history refresh so the new/updated session
            // appears in the history dropdown immediately after completion.
            setHistoryRefresh((c) => c + 1);
          } else if (evt === 'error') {
            throw new Error(payload.detail || 'Agent error');
          }
        }
      }

      // Post-stream safety net: if the done event was never received (e.g.
      // the SSE frame was malformed or the connection dropped before done),
      // show a fallback so the user isn't left staring at a blank chat.
      if (!doneReceived) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: "Generation complete. Your content has been saved — reload this session to see it.",
          artifacts: [],
          timestamp: new Date().toISOString(),
        }]);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Could not reach the authoring agent — please try again.');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, streaming, sessionId, domain]);

  // ── Artifact actions ────────────────────────────────────────────────────────

  // Save to library only (no module linking, not immediately learner-visible)
  async function handleSaveToLibrary(artifactId) {
    try {
      const res = await fetch(`${API_BASE}/coach/authoring-chat/sessions/${sessionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ artifact_ids: [artifactId], publish: false }),
      });
      if (!res.ok) throw new Error(await res.text());
      setArtifacts((prev) => prev.map((a) =>
        a.artifact_id === artifactId ? { ...a, status: 'approved' } : a,
      ));
      setSuccessMsg('Saved to Content Library. Use "Publish to Module" to make it learner-visible.');
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    }
  }

  // Open module picker modal for publish
  function handleOpenPublishModal(artifactIds) {
    setPublishModal({
      open: true,
      artifactIds: Array.isArray(artifactIds) ? artifactIds : [artifactIds],
      selectedModuleId: domainModules.length > 0 ? domainModules[0].module_id : '',
      selectedTutorialId: '',
    });
  }

  // Execute publish after module is selected
  async function handleConfirmPublish() {
    const { artifactIds, selectedModuleId, selectedTutorialId } = publishModal;
    if (!selectedModuleId) { setError('Please select a module.'); return; }
    setPublishModal((prev) => ({ ...prev, open: false }));
    try {
      const res = await fetch(`${API_BASE}/coach/authoring-chat/sessions/${sessionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          artifact_ids: artifactIds,
          publish: true,
          target_module_id: selectedModuleId,
          target_tutorial_id: selectedTutorialId || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setArtifacts((prev) => prev.map((a) =>
        artifactIds.includes(a.artifact_id) ? { ...a, status: 'approved' } : a,
      ));
      const modName = domainModules.find((m) => m.module_id === selectedModuleId)?.display_name || selectedModuleId;
      setSuccessMsg(`✓ Published to "${modName}". Learners can now access this content.`);
      setTimeout(() => setSuccessMsg(null), 6000);
    } catch (err) {
      setError(`Publish failed: ${err.message}`);
    }
  }

  // Legacy alias (used by some older call sites, now defaults to Save to Library)
  const handleApprove = handleSaveToLibrary;

  async function handleReject(artifactId, feedback) {
    try {
      const res = await fetch(`${API_BASE}/coach/authoring-chat/sessions/${sessionId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ artifact_ids: [artifactId], feedback: feedback || '' }),
      });
      if (!res.ok) throw new Error(await res.text());
      setArtifacts((prev) => prev.map((a) =>
        a.artifact_id === artifactId ? { ...a, status: 'rejected' } : a,
      ));
    } catch (err) {
      setError(`Reject failed: ${err.message}`);
    }
  }

  async function handleEdit(artifactId, updatedData) {
    try {
      const res = await fetch(
        `${API_BASE}/coach/authoring-chat/sessions/${sessionId}/artifacts/${artifactId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ data: updatedData }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setArtifacts((prev) => prev.map((a) =>
        a.artifact_id === artifactId ? { ...a, data: updatedData, status: 'edited' } : a,
      ));
    } catch (err) {
      setError(`Edit failed: ${err.message}`);
    }
  }

  function handleRegenerate(artifactId) {
    const artifact = artifacts.find((a) => a.artifact_id === artifactId);
    const name = artifact?.display_name || artifact?.artifact_type || 'this artifact';
    setInput(`Please regenerate the ${artifact?.artifact_type?.replace(/_/g, ' ') || 'content'} "${name}" with improvements.`);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const pendingArtifacts = artifacts.filter((a) => a.status === 'pending' || a.status === 'edited');
  const showThinking = streaming || reasoningSteps.length > 0;

  function getMessageArtifacts(msg) {
    if (!msg.artifacts || msg.artifacts.length === 0) return [];
    return msg.artifacts.map((a) => {
      const latest = artifacts.find((la) => la.artifact_id === a.artifact_id);
      return latest || a;
    });
  }

  return (
    <>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {showPicker && (
        <LibraryPickerModal
          domain={domain}
          onImport={handleLibraryImport}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div style={S.page}>
        {/* Pre-fill hint banner — shown when navigated from Item Bank / Domain Health generate */}
        {urlPrompt && (
          <div style={{
            background: 'rgba(99,102,241,0.12)', borderBottom: '1px solid rgba(99,102,241,0.3)',
            padding: '10px 20px', fontSize: 12, color: '#a78bfa', display: 'flex',
            alignItems: 'center', gap: 10, flexShrink: 0,
          }}>
            <span>✨</span>
            <span>
              Prompt pre-filled from{' '}
              {urlArtifactType === 'cat_item' ? 'Item Bank' : urlArtifactType === 'flashcard' ? 'Flashcard Cards' : 'Domain Health'}.
              {' '}Review the prompt below, adjust if needed, then click <strong>Send</strong> to generate.
              Items will appear as artifacts — <strong>Approve</strong> each one before it is saved.
            </span>
            <button
              style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', marginLeft: 'auto', fontSize: 11 }}
              onClick={() => { setInput(''); window.history.replaceState({}, '', window.location.pathname); }}
            >
              ✕ dismiss
            </button>
          </div>
        )}

        {/* Top bar */}
        <div style={S.topBar}>
          <div style={S.topBarLeft}>
            <button
              onClick={() => navigate('/coach')}
              style={{
                background: 'none', border: 'none', color: '#94a3b8',
                cursor: 'pointer', padding: '4px 8px', fontSize: 18,
                display: 'flex', alignItems: 'center', marginRight: 4,
              }}
              title="Back to Dashboard"
            >←</button>
            <span>🏗️</span>
            <span>Content Studio</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              style={S.domainSelect}
              value={domain}
              onChange={(e) => { setDomain(e.target.value); startNewSession(); }}
              disabled={domainOptions.length === 0 || streaming}
            >
              {domainOptions.length === 0 && <option value="">No domains assigned</option>}
              {domainOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              style={S.importBtn}
              onClick={() => setShowPicker(true)}
              disabled={streaming || importing || !domain}
            >
              📚 Edit from Library
            </button>
            {sessionHistory.length > 0 && (
              <button
                style={{ ...S.newSessionBtn, borderColor: '#94a3b8', color: '#94a3b8' }}
                onClick={() => setShowHistory(!showHistory)}
                disabled={streaming}
              >
                📋 History ({sessionHistory.length})
              </button>
            )}
            {sessionId && (
              <button style={S.newSessionBtn} onClick={startNewSession} disabled={streaming}>
                + New Session
              </button>
            )}
          </div>
        </div>

        {error && <div style={S.errorBanner}>{error}</div>}
        {successMsg && <div style={S.successBanner}>{successMsg}</div>}

        {/* Session history dropdown */}
        {showHistory && sessionHistory.length > 0 && (
          <div style={{
            background: '#161922', borderBottom: '1px solid #252836',
            padding: '8px 20px', flexShrink: 0, maxHeight: 200, overflowY: 'auto',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
              Previous Sessions
            </div>
            {sessionHistory.map((s) => {
              const isActive = s.session_id === sessionId;
              const firstMsg = (s.messages || []).find((m) => m.role === 'coach');
              const preview = firstMsg?.content?.slice(0, 80) || 'Empty session';
              const artifactCount = (s.pending_artifacts || []).length;
              return (
                <div
                  key={s.session_id}
                  onClick={() => !isActive && loadSession(s.session_id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 10px', borderRadius: 6, marginBottom: 4, cursor: isActive ? 'default' : 'pointer',
                    background: isActive ? 'rgba(79,142,247,0.15)' : 'rgba(15,17,23,0.4)',
                    border: isActive ? '1px solid #4f8ef7' : '1px solid transparent',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {preview}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      {new Date(s.created_at).toLocaleDateString()} · {artifactCount} artifact{artifactCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {isActive && <span style={{ fontSize: 11, color: '#4f8ef7', fontWeight: 600, marginLeft: 8 }}>ACTIVE</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Message thread with inline artifact cards */}
        <div style={S.thread} ref={threadRef}>
          {messages.length === 0 && !streaming && !importing && (
            <div style={S.emptyState}>
              <div style={{ fontSize: 48 }}>🏗️</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>Content Studio</div>
              <div>Create new content or edit existing items from the Content Library.</div>
              <div style={{ fontSize: 12, maxWidth: 440, marginTop: 4 }}>
                Try: "Create a beginner module on prompt engineering with a quiz and a reflection exercise."
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, maxWidth: 440 }}>
                You can review, edit, and approve each piece of content before it goes to the library.
                Keep chatting to refine the content until you're happy with it.
              </div>
              <button
                style={{ ...S.importBtn, marginTop: 16, padding: '10px 24px', fontSize: 14 }}
                onClick={() => setShowPicker(true)}
                disabled={!domain}
              >
                📚 Edit Existing Content from Library
              </button>
            </div>
          )}

          {importing && (
            <div style={S.bubbleWrap('left')}>
              <div style={S.streamingIndicator}>
                <div style={S.streamingDot} />
                <span>Importing from Content Library…</span>
              </div>
            </div>
          )}

          {messages.map((msg, i) => {
            const isUser = msg.role === 'user' || msg.role === 'coach' || msg.role === 'learner';
            const isSystem = msg.role === 'system';
            const msgArtifacts = getMessageArtifacts(msg);

            return (
              <React.Fragment key={i}>
                {/* Text bubble */}
                {msg.content && (
                  <div style={S.bubbleWrap(isUser ? 'right' : 'left')}>
                    <div
                      style={S.bubble(isUser ? 'user' : 'assistant')}
                      data-role={isUser ? 'user' : 'assistant'}
                      className={isUser ? 'chat-message user-message' : 'chat-message assistant-message'}
                    >
                      <div style={S.roleLabel(msg.role)}>
                        {isUser ? 'You' : isSystem ? '📋 System' : '🤖 Content Agent'}
                      </div>
                      {(() => {
                        const c = msg.content;
                        if (typeof c !== 'string') return c;
                        // If the text is mostly JSON, replace with friendly message
                        const trimmed = c.trim();
                        const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('```json') || trimmed.startsWith('[{');
                        if (looksLikeJson && msgArtifacts.length > 0) {
                          return 'Here\'s what I\'ve created for you. Review each item below and approve, edit, or ask me to regenerate.';
                        }
                        // If text has embedded JSON but also has prose before it, extract the prose
                        if (looksLikeJson && msgArtifacts.length === 0) {
                          const { cleanText, extractedArtifacts } = extractEmbeddedArtifacts(c);
                          if (extractedArtifacts.length > 0) {
                            // Dynamically add extracted artifacts to the message
                            if (!msg._extractedRendered) {
                              msg._extractedRendered = true;
                              msg.artifacts = [...(msg.artifacts || []), ...extractedArtifacts];
                            }
                            return cleanText || 'Here\'s the updated content. Review each item below.';
                          }
                        }
                        return c;
                      })()}
                    </div>
                  </div>
                )}

                {/* Artifact cards inline after the message */}
                {!isUser && msgArtifacts.length > 0 && (
                  <div style={S.artifactSection} className="content-artifact" data-testid="artifact-section">
                    <div style={S.artifactSectionHeader}>
                      {isSystem ? 'Imported' : 'Generated'} Content ({msgArtifacts.length} item{msgArtifacts.length !== 1 ? 's' : ''})
                    </div>
                    {msgArtifacts.map((art) => (
                      <StructuredArtifactCard
                        key={art.artifact_id}
                        artifact={art}
                        onApprove={handleSaveToLibrary}
                        onPublish={handleOpenPublishModal}
                        onReject={handleReject}
                        onEdit={handleEdit}
                        onRegenerate={handleRegenerate}
                      />
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* Orphan artifacts (generated but not attached to a message) */}
          {artifacts.length > 0 && messages.every((m) => !m.artifacts || m.artifacts.length === 0) && (
            <div style={S.artifactSection} className="content-artifact" data-testid="artifact-section">
              <div style={S.artifactSectionHeader}>
                Content ({artifacts.length} item{artifacts.length !== 1 ? 's' : ''})
              </div>
              {artifacts.map((art) => (
                <StructuredArtifactCard
                  key={art.artifact_id}
                  artifact={art}
                  onApprove={handleSaveToLibrary}
                  onPublish={handleOpenPublishModal}
                  onReject={handleReject}
                  onEdit={handleEdit}
                  onRegenerate={handleRegenerate}
                />
              ))}
            </div>
          )}

          {/* ── Chain-of-thought panel ─────────────────────────────────────────
               Visible while generating AND after completion (collapses when done).
               Mimics Cursor's thinking panel: shows numbered steps as the agent
               reasons through content creation in real time.
          ─────────────────────────────────────────────────────────────────── */}
          {showThinking && (
            <div style={{
              margin: '8px 20px 4px',
              borderRadius: 10,
              border: streaming
                ? '1px solid rgba(249,115,22,0.35)'
                : '1px solid rgba(100,116,139,0.25)',
              overflow: 'hidden',
              background: streaming ? 'rgba(13,12,11,0.9)' : 'rgba(15,17,23,0.6)',
              flexShrink: 0,
              transition: 'border-color 0.3s, background 0.3s',
            }}>
              {/* Status header shown ABOVE the step list when streaming */}
              {streaming && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px',
                  background: 'rgba(249,115,22,0.06)',
                  borderBottom: '1px solid rgba(249,115,22,0.12)',
                  fontSize: 12,
                }}>
                  <span style={{ fontSize: 14 }}>🤔</span>
                  <span style={{ color: '#f97316', fontWeight: 700, letterSpacing: '-0.01em' }}>
                    Thinking…
                  </span>
                  <span style={{ marginLeft: 'auto', color: 'rgba(245,240,235,0.4)', fontSize: 11 }}>
                    {reasoningSteps.length > 0 ? `${reasoningSteps.length} step${reasoningSteps.length !== 1 ? 's' : ''}` : ''}
                  </span>
                  <span style={{
                    display: 'inline-block', width: 10, height: 10,
                    border: '2px solid rgba(249,115,22,0.25)',
                    borderTopColor: '#f97316', borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                </div>
              )}
              {!streaming && reasoningSteps.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px',
                  background: 'rgba(100,116,139,0.06)',
                  borderBottom: '1px solid rgba(100,116,139,0.12)',
                  fontSize: 12,
                }}>
                  <span style={{ fontSize: 13 }}>✅</span>
                  <span style={{ color: '#94a3b8', fontWeight: 600 }}>
                    Reasoning complete · {reasoningSteps.length} step{reasoningSteps.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <ReasoningPanel
                reasoningSteps={reasoningSteps}
                liveText={liveText}
                toolActions={toolActions}
                isStreaming={streaming}
                defaultOpen={true}
                title=""
              />
            </div>
          )}

          {streaming && !showThinking && (
            <div style={S.bubbleWrap('left')}>
              <div style={S.streamingIndicator}>
                <div style={S.streamingDot} />
                <span>Generating content…</span>
              </div>
            </div>
          )}

          {/* Usage summary */}
          {usage && !streaming && (
            <div style={S.usageSummary}>
              <span>Model: {usage.model}</span>
              <span>Tokens: {(usage.total_tokens || 0).toLocaleString()}</span>
              {usage.cached_tokens > 0 && <span>Cached: {usage.cached_tokens.toLocaleString()}</span>}
              <span>Latency: {((usage.latency_ms || 0) / 1000).toFixed(1)}s</span>
            </div>
          )}
        </div>

        {/* Pending artifact count + action bar */}
        {pendingArtifacts.length > 0 && !streaming && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            padding: '8px 20px', background: 'rgba(245,158,11,0.08)',
            borderTop: '1px solid rgba(245,158,11,0.2)', fontSize: 13, flexShrink: 0,
          }}>
            <span style={{ color: '#f59e0b' }}>
              {pendingArtifacts.length} pending item{pendingArtifacts.length !== 1 ? 's' : ''} awaiting review
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{
                  background: 'transparent', color: '#94a3b8', border: '1px solid #334155',
                  borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}
                onClick={() => pendingArtifacts.forEach((a) => handleSaveToLibrary(a.artifact_id))}
                title="Save all to Content Library (not yet learner-visible)"
              >
                Save All to Library
              </button>
              <button
                style={{
                  background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
                onClick={() => handleOpenPublishModal(pendingArtifacts.map((a) => a.artifact_id))}
                title="Publish to a curriculum module so learners can access the content"
              >
                ✓ Publish to Module…
              </button>
            </div>
          </div>
        )}

        {/* Publish-to-module modal */}
        {publishModal.open && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}>
            <div style={{
              background: '#1e293b', border: '1px solid #334155', borderRadius: 12,
              padding: '28px 32px', width: 480, maxWidth: '95vw', maxHeight: '80vh',
              overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}>
              <h3 style={{ margin: '0 0 6px', color: '#f1f5f9', fontSize: 17 }}>Publish to Module</h3>
              <p style={{ margin: '0 0 20px', color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
                Select which curriculum module this content belongs to.
                A lesson will be automatically created and learners will see it immediately.
              </p>

              <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6, fontWeight: 600 }}>
                TARGET MODULE
              </label>
              {domainModules.length === 0 ? (
                <p style={{ color: '#f59e0b', fontSize: 13 }}>
                  No modules found for this domain. Create modules in the Curriculum Builder first.
                </p>
              ) : (
                <select
                  style={{
                    width: '100%', background: '#0f172a', color: '#f1f5f9',
                    border: '1px solid #334155', borderRadius: 6, padding: '8px 10px',
                    fontSize: 14, marginBottom: 16, cursor: 'pointer',
                  }}
                  value={publishModal.selectedModuleId}
                  onChange={(e) => setPublishModal((prev) => ({
                    ...prev, selectedModuleId: e.target.value, selectedTutorialId: '',
                  }))}
                >
                  {domainModules.map((m) => (
                    <option key={m.module_id} value={m.module_id}>
                      {m.display_name || m.module_id}
                    </option>
                  ))}
                </select>
              )}

              {/* Optionally add to existing lesson */}
              {(() => {
                const selMod = domainModules.find((m) => m.module_id === publishModal.selectedModuleId);
                const existingTuts = selMod?.tutorials || [];
                if (existingTuts.length === 0) return null;
                return (
                  <>
                    <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6, fontWeight: 600 }}>
                      ADD TO LESSON (optional — leave blank to create a new lesson)
                    </label>
                    <select
                      style={{
                        width: '100%', background: '#0f172a', color: '#f1f5f9',
                        border: '1px solid #334155', borderRadius: 6, padding: '8px 10px',
                        fontSize: 14, marginBottom: 16, cursor: 'pointer',
                      }}
                      value={publishModal.selectedTutorialId}
                      onChange={(e) => setPublishModal((prev) => ({
                        ...prev, selectedTutorialId: e.target.value,
                      }))}
                    >
                      <option value="">— Create new lesson —</option>
                      {existingTuts.map((t) => (
                        <option key={t.tutorial_id} value={t.tutorial_id}>{t.title || t.tutorial_id}</option>
                      ))}
                    </select>
                  </>
                );
              })()}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  style={{
                    background: 'transparent', color: '#94a3b8', border: '1px solid #334155',
                    borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer',
                  }}
                  onClick={() => setPublishModal((prev) => ({ ...prev, open: false }))}
                >
                  Cancel
                </button>
                <button
                  style={{
                    background: domainModules.length > 0 ? '#22c55e' : '#334155',
                    color: '#fff', border: 'none', borderRadius: 6,
                    padding: '8px 20px', fontSize: 13, fontWeight: 600,
                    cursor: domainModules.length > 0 ? 'pointer' : 'not-allowed',
                  }}
                  onClick={handleConfirmPublish}
                  disabled={domainModules.length === 0 || !publishModal.selectedModuleId}
                >
                  ✓ Publish Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Input bar */}
        <div style={S.inputBar}>
          <textarea
            style={S.input}
            rows={1}
            placeholder={
              artifacts.length > 0
                ? 'Ask to refine, regenerate, or create more content…'
                : 'Describe the content you\'d like to create…'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={streaming}
          />
          <button
            style={S.sendBtn(streaming || !input.trim())}
            onClick={sendMessage}
            disabled={streaming || !input.trim()}
          >
            {streaming ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </>
  );
}
